"use client";
import React, { useMemo, useState } from "react";
import Link from "next/link";
import { cn } from "@/lib";
import AppContainer from "../appContainer";
import { globalRawServicesData } from "@/constants/globalRawServicesData/globalRawServicesData";
import { OlMinimalisticMagnifer } from "solar-icon-react/ol";
import { BdAltArrowLeft, BdCalculator } from "solar-icon-react/bd";
import { IRawInquiriesResData, IRawInquiryItem } from "@/interfaces";
import { getClientServiceKeyByAPIFlag } from "@/lib/adapters/serviceFlagAdapter";

const CAT_ID_TO_KEY: Record<string, string> = {
  "1": "banking",
  "2": "vehicle",
  "3": "government",
  "4": "legal",
  "7": "free",
};

const ICON_BG: Record<string, string> = {
  banking: "bg-emerald-mint/10 text-emerald-mint",
  vehicle: "bg-sky-bliss/10 text-sky-bliss",
  government: "bg-teal-ocean/10 text-teal-ocean",
  legal: "bg-sunset-gold/10 text-sunset-gold",
  free: "bg-yellow-500/10 text-yellow-500",
};

const POPULAR_FLAGS = [
  "traffic_fines",
  "plate",
  "cards",
  "ibans",
  "cheque_v2",
  "validate",
  "internal_credit_score",
  "identity",
  "sana",
  "postal_tracking",
  "educational_exemption",
  "postal_code",
  "returned_check",
];

const SERVICE_ALIAS_MAP: Record<string, Omit<DisplayService, "apiName">> = {
  internal_credit_score: {
    key: "internal-bank-validation",
    flag: "internal_credit_score",
    adaptedName: "\u0627\u0639\u062a\u0628\u0627\u0631\u0633\u0646\u062c\u06cc \u0646\u0648\u06cc\u0646",
    href:
      "/services/" +
      encodeURIComponent(
        "\u0627\u0639\u062a\u0628\u0627\u0631\u0633\u0646\u062c\u06cc",
      ),
    icon: globalRawServicesData.Bank_Validation.icon as React.ReactElement,
    category: "banking",
  },
  loan_installment_calculator: {
    key: "loan-installment-calculator",
    flag: "loan_installment_calculator",
    adaptedName: "محاسبه اقساط وام",
    href: "/services/محاسبه-اقساط-وام",
    icon: <BdCalculator />,
    category: "free",
  },
  inflation_calculator: {
    key: "inflation-calculator",
    flag: "inflation_calculator",
    adaptedName: "ماشین حساب تورم",
    href: "/services/ماشین-حساب-تورم",
    icon: <BdCalculator />,
    category: "free",
  },
  deposit_interest_calculator: {
    key: "deposit-interest-calculator",
    flag: "deposit_interest_calculator",
    adaptedName: "محاسبه سود سپرده بانکی",
    href: "/services/محاسبه-سود-سپرده-بانکی",
    icon: <BdCalculator />,
    category: "free",
  },
  age_calculator: {
    key: "age-calculator",
    flag: "age_calculator",
    adaptedName: "محاسبه سن آنلاین",
    href: "/services/محاسبه-سن-آنلاین",
    icon: globalRawServicesData.Age_Calculator.icon as React.ReactElement,
    category: "free",
  },
  payroll_calculator_1405: {
    key: "payroll-calculator-1405",
    flag: "payroll_calculator_1405",
    adaptedName: "ماشین حساب حقوق و دستمزد ۱۴۰۵",
    href: "/services/ماشین-حساب-حقوق-و-دستمزد-۱۴۰۵",
    icon: globalRawServicesData.Payroll_Calculator_1405.icon as React.ReactElement,
    category: "free",
  },
};

const HIDDEN_SERVICE_FLAGS = new Set(["toll"]);

type DisplayService = {
  key: string;
  flag: string;
  apiName: string;
  adaptedName: string;
  href: string;
  icon: React.ReactElement;
  category: string;
};

const normalizeText = (str: string): string =>
  str.replace(/ي/g, "ی").replace(/ك/g, "ک").replace(/[‌‍]/g, "").trim();

const getServiceHref = (item: IRawInquiryItem, fallbackHref: string): string => {
  const seoUrl = item.seo_url?.trim();
  if (!seoUrl) return fallbackHref;
  if (seoUrl.startsWith("/")) return seoUrl;
  if (/^https?:\/\//i.test(seoUrl)) return seoUrl;
  return `/services/${seoUrl}`;
};

const ServiceCard = ({ service }: { service: DisplayService }) => {
  const iconBg = ICON_BG[service.category] ?? "bg-gray-100 text-gray-400";
  return (
    <Link
      href={service.href}
      className="group flex flex-col items-center gap-2 rounded-xl p-3 text-center transition-all duration-200 hover:bg-gray-50"
    >
      <div
        className={cn(
          "flex h-12 w-12 items-center justify-center rounded-xl transition-transform duration-200 group-hover:scale-110",
          iconBg,
        )}
      >
        {React.cloneElement(
          service.icon as React.ReactElement<{ className?: string }>,
          { className: "size-6" },
        )}
      </div>
      <span className="line-clamp-2 text-[11px] leading-tight font-medium text-gray-500 transition-colors group-hover:text-gray-700">
        {service.adaptedName}
      </span>
    </Link>
  );
};

const QuickAccessServicesSection = ({
  servicesData,
}: {
  servicesData: IRawInquiriesResData | null;
}) => {
  const [query, setQuery] = useState("");

  // Built once from server-fetched servicesData; search/filter are pure in-memory ops.
  const allServices = useMemo<DisplayService[]>(() => {
    if (!servicesData) {
      return Object.values(globalRawServicesData)
        .filter((s) => !s.disabled && s.icon)
        .map((s) => ({
          key: s.key,
          flag: s.api.apiKey as string,
          apiName: s.label.adapted,
          adaptedName: s.label.adapted,
          href: s.href.rewrite,
          icon: s.icon as React.ReactElement,
          category: s.category,
        }));
    }

    const seenFlags = new Set<string>();
    return (
      Object.entries(servicesData) as [string, IRawInquiryItem[]][]
    ).flatMap(([catId, items]) => {
      const category = CAT_ID_TO_KEY[catId];
      if (!category) return [];

      return items
        .filter(
          (item) =>
            item.status !== "inactive" &&
            item.status !== "unavailability" &&
            !HIDDEN_SERVICE_FLAGS.has(item.flag),
        )
        .flatMap((item): DisplayService[] => {
          const flag = item.flag as string;
          if (seenFlags.has(flag)) return [];
          if (SERVICE_ALIAS_MAP[flag]) {
            seenFlags.add(flag);
            return [
              {
                ...SERVICE_ALIAS_MAP[flag],
                apiName: item.name,
                adaptedName: item.name || SERVICE_ALIAS_MAP[flag].adaptedName,
                href: getServiceHref(item, SERVICE_ALIAS_MAP[flag].href),
              },
            ];
          }
          const serviceKey = getClientServiceKeyByAPIFlag(item.flag);
          if (!serviceKey) return [];
          const s = globalRawServicesData[serviceKey];
          if (!s?.icon) return [];
          seenFlags.add(flag);
          return [
            {
              key: `${s.key}-${flag}`,
              flag,
              apiName: item.name,
              adaptedName: item.name || s.label.adapted,
              href: getServiceHref(item, s.href.rewrite),
              icon: s.icon as React.ReactElement,
              category,
            },
          ];
        });
    });
  }, [servicesData]);

  const byFlag = useMemo(
    () => new Map(allServices.map((s) => [s.flag, s])),
    [allServices],
  );

  const services = useMemo<DisplayService[]>(() => {
    const rawQuery = query.trim();
    if (rawQuery) {
      // AND logic across both label and API name, with Arabic/Persian normalisation.
      const words = normalizeText(rawQuery).split(/\s+/).filter(Boolean);
      return allServices.filter((s) => {
        const haystack = normalizeText(`${s.adaptedName} ${s.apiName}`);
        return words.every((w) => haystack.includes(w));
      });
    }

    return POPULAR_FLAGS.flatMap((flag) => {
      const s = byFlag.get(flag);
      return s ? [s] : [];
    });
  }, [query, allServices, byFlag]);

  const hasQuery = query.trim().length > 0;

  return (
    <section className="relative z-30 mt-[-120px] mb-16 w-full sm:mt-[-160px] md:mt-[-160px] lg:mt-[-180px] lg:mb-24 xl:mt-[-200px]">
      <AppContainer className="h-full max-w-[720px] lg:max-w-[920px] xl:max-w-[1020px]">
        <div
          className={cn(
            "my--3 overflow-hidden rounded-2xl bg-white",
            "border border-gray-200/60",
            "shadow-[0_4px_24px_rgba(0,0,0,0.06),0_20px_60px_-8px_rgba(90,75,210,0.16)]",
          )}
        >
          {/* Card header */}
          <div className="flex items-center justify-between px-5 pt-4 pb-3">
            <div className="flex items-center gap-2">
              <OlMinimalisticMagnifer
                className={cn(
                  "size-4 transition-colors duration-200",
                  hasQuery ? "text-primary-main" : "hidden",
                )}
              />
              <span className="text-sm font-semibold text-gray-600">
                {hasQuery ? "نتایج جستجو" : "خدمات پرکاربرد"}
              </span>
            </div>
            <button
              onClick={() =>
                document
                  .getElementById("core-services-landing")
                  ?.scrollIntoView({ behavior: "smooth" })
              }
              className="text-primary-main/60 hover:text-primary-main flex cursor-pointer items-center gap-1 text-sm font-medium transition-colors outline-none"
            >
              مشاهده همه خدمات
              <BdAltArrowLeft size={13} />
            </button>
          </div>

          {/* Search */}
          <div
            className={cn(
              "mx-3 flex items-center gap-3 rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 transition-all duration-200",
              "focus-within:border-primary-main/30 focus-within:bg-white focus-within:shadow-sm",
            )}
          >
            <OlMinimalisticMagnifer
              className={cn(
                "size-4 shrink-0 transition-colors duration-200",
                hasQuery ? "text-primary-main" : "text-gray-400",
              )}
            />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="جستجو... (خلافی، شبا، گذرنامه، هویت)"
              className="w-full bg-transparent text-sm text-gray-700 placeholder:text-gray-400 focus:outline-none"
              dir="rtl"
            />
            {hasQuery && (
              <button
                onClick={() => setQuery("")}
                className="shrink-0 rounded-full px-2 py-0.5 text-[11px] text-gray-400 transition-colors hover:bg-gray-200 hover:text-gray-600"
              >
                پاک کردن
              </button>
            )}
          </div>

          {/* Service grid */}
          <div className="p-4 pt-3">
            {services.length > 0 ? (
              <div className="grid grid-cols-3 gap-1 md:grid-cols-4 lg:grid-cols-6">
                {services.map((service) => (
                  <ServiceCard key={service.key} service={service} />
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-10 text-gray-400">
                <OlMinimalisticMagnifer className="mb-2 size-8 opacity-20" />
                <p className="text-xs font-medium">خدمتی یافت نشد</p>
                <p className="mt-1 text-[11px] opacity-60">
                  نام دیگری را امتحان کنید یا همه خدمات را ببینید
                </p>
              </div>
            )}
          </div>
        </div>
      </AppContainer>
    </section>
  );
};

export default QuickAccessServicesSection;
